import { cn } from '@commenda/utils';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { CALENDAR_BG_WEEKEND } from './calendar.constants';
import { RbcEvent } from './calendar.types';
import { useCalendarNav } from './CalendarContext';
import CalendarDateHeader from './CalendarDateHeader';
import CalendarEventPill from './CalendarEventPill';
import CalendarShowMore from './CalendarShowMore';

// Approximate pixel heights for layout components (py-1=8px + leading-15px, etc.)
const PILL_H = 23;
const PILL_GAP = 2; // gap-0.5
const HEADER_H = 30; // CalendarDateHeader
const CONTENT_V_PAD = 4; // py-0.5 top + bottom
const SHOWMORE_H = 28; // py-1 + leading-5
const SECTION_GAP = 8; // gap-2 between pills block and showMore
const MAX_PILLS = 4;

const calcVisibleCount = (cellHeight: number, totalEvents: number): number => {
  if (cellHeight === 0 || totalEvents === 0) return 0;
  const available = cellHeight - HEADER_H - CONTENT_V_PAD;

  // Can all events fit without a "more" row?
  const fitAll = Math.floor((available + PILL_GAP) / (PILL_H + PILL_GAP));
  if (totalEvents <= Math.min(MAX_PILLS, fitAll))
    return Math.min(MAX_PILLS, fitAll);

  // Reserve space for the "more" row and recalculate
  const fitWithMore = Math.floor(
    (available - SHOWMORE_H - SECTION_GAP + PILL_GAP) / (PILL_H + PILL_GAP)
  );
  return Math.min(MAX_PILLS, Math.max(0, fitWithMore));
};

interface CalendarDayCellProps {
  date: Date;
  events: RbcEvent[];
  isOffRange?: boolean;
  onSelectEvent: (event: RbcEvent) => void;
}

const CalendarDayCell = ({
  date,
  events,
  isOffRange,
  onSelectEvent,
}: CalendarDayCellProps) => {
  const { navigateToWeek } = useCalendarNav();
  const cellRef = useRef<HTMLDivElement>(null);
  const [cellHeight, setCellHeight] = useState(0);

  useEffect(() => {
    const el = cellRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setCellHeight(entry!.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const isWeekend = date.getDay() === 0 || date.getDay() === 6;
  const isPast = moment(date).isBefore(moment(), 'day');

  const visibleCount = calcVisibleCount(cellHeight, events.length);
  const visible = events.slice(0, visibleCount);
  const overflow = events.length - visibleCount;
  const eventsOpacity = isPast ? (isWeekend ? 0.5 : 0.25) : 1;

  return (
    <div
      ref={cellRef}
      className={cn('flex flex-col h-full', !isWeekend && 'bg-white')}
      style={isWeekend ? { backgroundColor: CALENDAR_BG_WEEKEND } : undefined}
    >
      <CalendarDateHeader
        date={date}
        label={String(date.getDate())}
        isOffRange={isOffRange}
        onDrillDown={() => navigateToWeek(date)}
      />
      <div className="flex flex-col px-2 py-0.5 gap-2">
        <div
          className="flex flex-col gap-0.5"
          style={{ opacity: eventsOpacity }}
        >
          {visible.map((event) => (
            <div
              key={event.id}
              onClick={() => onSelectEvent(event)}
              className="cursor-pointer hover:opacity-80 active:opacity-60 transition-opacity duration-100 ease motion-reduce:transition-none"
            >
              <CalendarEventPill event={event} />
            </div>
          ))}
        </div>
        {overflow > 0 && (
          <div style={{ opacity: eventsOpacity }}>
            <CalendarShowMore count={overflow} slotDate={date} />
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarDayCell;
